/**
 * COMBAT ACTION TABLE — action time is AUTHORITATIVE; the animation visualizes it.
 *
 * This is the single source of truth for melee timing, damage, and hit volumes, read
 * identically by client prediction (main.ts) and server authority (rooms.ts applyMelee).
 * It replaces the unitless fractions that used to live in avatar.ts (SWORD_CONTACT_OPEN/
 * CLOSE, SWORD_COMBO_CANCEL/GRACE) and main.ts (SWORD_LUNGE_SPEED) — those were client-only,
 * so the server could not know when a sword was dangerous.
 *
 * `arc` keys into HIT_ARCS (shared/src/hitVolumeData.ts — GENERATED by the bake). The bake
 * VALIDATES this table against the real clips: if an authored active window does not contain
 * the blade's actual sweep, the bake fails. See the spec, §5.
 *
 * Phase 5 of docs/hit-detection-impl-plan.md.
 */

/** ROLLOUT FLAG. On → action table + swept arcs (client AND server). Off → the legacy
 *  cone/instantaneous path. Both sides read this const so they switch together. */
export const SWEPT_MELEE = true;

export type CombatActionId =
  | "sword_light_1"
  | "sword_light_2"
  | "sword_light_3"
  | "sword_light_4"
  | "sword_parry"
  | "sword_riposte"
  | "sword_launcher"
  | "sword_air_1"
  | "sword_air_2"
  | "sword_gpound"
  | "sword_dodge_f"
  | "sword_dodge_b"
  | "sword_dodge_l"
  | "sword_dodge_r"
  | "sword_roll_f"
  | "sword_roll_b"
  | "sword_roll_l"
  | "sword_roll_r"
  | "sword_roll_bl"
  | "sword_roll_br"
  // THE 2H GREATSWORD MODE (Valheim-style — docs/2h-combat-spec.md; lab-only for now).
  | "gs_light_1"
  | "gs_light_2"
  | "gs_light_3"
  | "gs_heavy";

/** Deterministic authored displacement. The TYPE lands now so the resolver has a seam;
 *  the CURVES are baked in item 3 (a separate spec). Every action's `movementCurve` is
 *  absent at the end of Phase 5 — travel still comes from the dash impulse. */
export interface MovementCurveSample {
  /** Normalized action time, 0..1. */
  t: number;
  /** Cumulative displacement in body-local metres. */
  forward: number;
  lateral: number;
}
export interface MovementCurve {
  samples: readonly MovementCurveSample[];
}

export interface CombatAction {
  id: CombatActionId;
  /** The ClipKey (see client/src/character/anim.ts) this action visualizes.
   *  VALIDATED against the clip by the bake — never read from it at runtime. */
  clip: string;
  /** Total action time — a GAMEPLAY TIMER, in gameplay ms. It is NOT "the clip's runtime at
   *  its playback rate": action time and clip time are different clocks that can run at
   *  different rates. See the ACTION TIME vs CLIP TIME note above COMBAT_ACTIONS before
   *  converting between them (the bake must). */
  durationMs: number;
  /** Active hit window = [startupMs, startupMs + activeMs). */
  startupMs: number;
  activeMs: number;
  /** Earliest ms into the action the next combo step may fire. Absent = not chainable.
   *  MUST be >= startupMs + activeMs whenever `arc` is set — THE CANCEL INVARIANT. A cancel
   *  inside the active window truncates the client's sweep of a swing the server still sweeps
   *  in full → phantom server hits. See the block comment above COMBAT_ACTIONS. */
  cancelIntoMs?: number;
  /** Ms past durationMs the chain stays open before resetting to step 1. */
  cancelGraceMs?: number;
  /** Damage. The SERVER READS THIS — never the client's reported dmg. */
  damage: number;
  /** Key into HIT_ARCS → the server resolves this action with the SWEPT path.
   *  Absent means one of two things, told apart by `damage`:
   *    damage === 0 → no hit volume at all (the dodges/rolls).
   *    damage  >  0 → a real attack the server resolves with the LEGACY CONE path
   *                   (today: sword_air only — see the note on its entry for why). */
  arc?: string;
  /** Authored travel. Absent through Phase 5 — see MovementCurve. */
  movementCurve?: MovementCurve;
  /** Invulnerability window [startMs, endMs] into the action. Absent = none. */
  iframeMs?: readonly [number, number];
  /** LAUNCH impulse (m/s, radially up) a hit from this action imparts to a groundable
   *  target — the DMC vertical game. The client CLAIMS it on drone hits (DroneHitMsg.up);
   *  the SERVER caps and applies it (drones.ts hit(): grounded/humanoid kinds only, never
   *  flyers). The launcher pops a target airborne; the aerials RE-LIFT one already up (the
   *  juggle). NEGATIVE = the SLAM (the GroundPound finisher): applied only to a target
   *  that is ALREADY airborne, spiking it down so attacker and victim hit the floor
   *  together. Absent = the hit never moves anyone vertically. */
  launch?: number;
}

/** Base blade damage at upgrade level 0 — matches combatStats(defaultUpgrades()).bladeDamage.
 *  The server scales by the attacker's upgrades at resolution time, exactly as bolts do.
 *  EXPORTED: the client scales per-action damage as `bladeDamage() × (def.damage / BLADE_BASE_DMG)`
 *  (the riposte's 3.5× rides this), and the server caps dhit claims the same way. */
export const BLADE_BASE_DMG = 30;
/** The riposte's damage multiple over a basic light hit (Elden Ring critical territory). */
const RIPOSTE_DMG = BLADE_BASE_DMG * 3.5;

// --- JUGGLE DECAY (user call 2026-07-24): a juggle must END. Each successive lifting hit
//     inside ONE airborne stint keeps LESS of the action's authored `launch` — the nth hit
//     lifts `launch × JUGGLE_DECAY^n` — and once that falls under JUGGLE_LIFT_MIN it lifts
//     NOTHING: the victim still takes the damage but sinks to the floor, so the string has a
//     natural end instead of an infinite re-lift ladder. The attacker's own air-rave float
//     (character.aerialHop) decays on the SAME curve, so the pair comes down together.
/** Per-hit retention of the launch impulse within one airborne stint. */
export const JUGGLE_DECAY = 0.7;
/** Below this (m/s) a re-lift is spent — the hit lifts nothing and stops sustaining the hang. */
export const JUGGLE_LIFT_MIN = 1.2;
/**
 * The nth lifting hit's actual impulse (m/s). `n` = lifting hits ALREADY taken this stint
 * (0 = the launcher / the first hit off the ground, which always lands full strength).
 * Returns 0 once the ladder is spent. Server authority (drones.ts), the lab mini-sim, and
 * the attacker's float all read THIS — one curve, no drift.
 */
export function juggleLift(base: number, n: number): number {
  // Exact integer-exponent decay (n = whole lifting hits), NOT Math.pow/pmath.pow:
  // a multiply loop is byte-deterministic across engines (the pmath-ban contract)
  // and exact where a transcendental pow is approximated. The ladder is spent in a
  // handful of hits, so the loop is trivially bounded.
  let v = base;
  for (let k = Math.max(0, Math.floor(n)); k > 0; k--) v *= JUGGLE_DECAY;
  return v < JUGGLE_LIFT_MIN ? 0 : v;
}

/** I-FRAME WINDOW (ms) for every evade — dodge AND crouch roll alike. character.ts evade()
 *  sets `iframeT = EVADE_IFRAMES` (0.18 s) unconditionally; the roll's ROLL_WINDOW (0.72 s) is
 *  its dash COAST window (how long the impulse glides), NOT invulnerability. Preserving today's
 *  behaviour therefore means 180 ms on all eight, not 720 on the rolls. */
const EVADE_IFRAME_MS = 180;

/**
 * ACTION TIME vs CLIP TIME — ONE CLOCK NOW (the DMC retime). Every action in this table,
 * attacks included, plays its clip at a REAL animation timeScale and its action clock is
 * clip time divided by that same rate — the old split (a 1.25 gameplay-timer divisor over a
 * 1.0× clip) is retired; avatar.ts's ladder has an `sd_attack` branch at SWORD_ATTACK_ANIM.
 *
 * CONVERSION, for the attacks:  clipTimeSeconds = actionElapsedMs / 1000 × SWORD_ATTACK_ANIM
 * (2.2 — see avatar.ts; the bake's ANIM_RATE/TIMER_DIVISOR rows mirror it). The action and
 * the clip now END TOGETHER: the full swing, follow-through included, renders inside
 * durationMs. Change the rate → re-derive this table and re-run `npm run bake:hitvolumes`.
 *
 * MEASURED clip durations (client/public/anim/manny/*.glb, read straight off the GLB), and
 * how each action's durationMs is derived — the divisor is the true animation timeScale on
 * EVERY row now:
 *   sd_attack / sd_attack2 / sd_attack3   1.6833 s  ÷ 2.20  →  765 ms   (SWORD_ATTACK_ANIM —
 *                                                                       the DMC retime)
 *   sd_air_begin                          0.5167 s  ÷ 1.00  →  517 ms   plays at 1× (see below)
 *   sd_dodge_f / sd_dodge_b               1.1833 s  ÷ 1.70  →  696 ms   (SWORD_DODGE_SPEED)
 *   sd_dodge_l / sd_dodge_r               1.0167 s  ÷ 1.70  →  598 ms
 *   sd_roll_*                             1.1000 s  ÷ 1.00  → 1100 ms   (SWORD_ROLL_ANIM — the dodge)
 *
 * WHY 2.2×. The SSH swings are authored long and cinematic (1.68 s). At 1× they read as
 * slow-motion flourishes and forced 0.5–1.2 s commitments; at 2.2× a full swing is ~765 ms
 * with chain points at 228–551 ms — an action-game light chain. The active windows below are
 * the SAME measured 12%–88% tip-travel spans as before, just expressed on the new clock.
 *
 * ============================================================================================
 * THE CANCEL INVARIANT:  cancelIntoMs >= startupMs + activeMs   FOR EVERY ACTION WITH AN ARC.
 * i.e. THE CANCEL WINDOW MAY NOT OVERLAP THE ACTIVE WINDOW. Pinned by shared/test/
 * combatactions.test.mts — do not weaken it to "usually true" or "true with margin".
 *
 * WHY. The client sweeps the arc incrementally as the swing plays and STOPS the instant a new
 * action starts (stepAction resets actionElapsedMs to 0). The SERVER never sweeps incrementally at
 * all: it resolves the swing ONCE, at PUNCH RECEIPT, in a single sweepArcVsCapsule(arc, 0, 1) call
 * over the action's WHOLE active window. It is told only the action id, so it cannot know the swing
 * was later cut short — and by the time the cancel happens the swing is already resolved and paid.
 * (It does NOT "keep sweeping" a cancelled swing; there is no server-side swing clock to cancel.)
 *
 * So any cancel that lands INSIDE a damage window desyncs the two BY CONSTRUCTION: the client
 * predicts a TRUNCATED arc while the server has already charged for the COMPLETE one. Every target
 * in the unswept remainder is damage the victim takes and the attacker's client never showed.
 *
 * With the old flat 606 (when sword_light_1's active window ran [133, 1142) on the pre-DMC
 * clock), a player mashing LMB cancelled at 606 ms — the client had swept 47% of arc 1, the
 * server had resolved 100% of it.
 * Note WHERE that 53% actually bit. Against PLAYERS the server was always the only authority (the
 * client's sweep of a player is a mere prediction), so the divergence surfaced as unpredicted PvP
 * damage; against DRONES the client's own truncated sweep is what CLAIMS the hit, so a cancelled
 * swing simply lost 53% of its drone hits. Same root cause, and the fix — making a mid-damage
 * cancel impossible — closes both.
 *
 * Setting cancelIntoMs to the END of the active window makes cancelling mid-damage IMPOSSIBLE
 * — not unlikely, impossible — so the client always sweeps the same span the server does. Same
 * "true by construction, not by margin" principle as sweepArcVsCapsule's subdivision invariance.
 * (Note this made sword_light_2 FASTER to chain out of, 606 → 502: it is the quick one and its
 * blade is spent by 502 ms. The blade being done is exactly what licenses the cancel.)
 *
 * The clamp is >= , not == : an action MAY hold the chain shut past its own active window (a
 * deliberately committal finisher). It may never open it early.
 *
 * SINCE THE DMC RETIME the punch is also SENT LATE: main.ts transmits `punch` only when the
 * tick that CLOSES the active window runs, and a swing cancelled before that close (dodge /
 * jump / mode-switch — all legal at ANY moment now) sends NOTHING. So a cancelled swing can
 * never be server-charged at all, whatever instant the cancel landed — the invariant above
 * remains the guard for the CHAIN path specifically: the next combo step may not open while
 * the current blade is still live, so the sweep the server bills is always the sweep the
 * client ran.
 * ============================================================================================
 *
 * ============================================================================================
 * THE ACTIVE WINDOWS (startupMs/activeMs) BELOW ARE **MEASURED**, NOT INHERITED — AND THEY ARE
 * THE ONE PLACE THIS TABLE DELIBERATELY DEPARTS FROM TODAY'S SHIPPED BEHAVIOUR.
 *
 * Legacy avatar.ts used ONE unitless pair — SWORD_CONTACT_OPEN/CLOSE = 0.20/0.80 of the action —
 * for ALL THREE attacks, giving every one of them [269, 1077) ms. But the three clips are timed
 * completely differently, so a one-size fraction lands on the wrong part of two of them:
 *   • sd_attack2 does 33% of its total blade travel BEFORE 269 ms — a big fast windup that was
 *     already swinging while the blade was still officially harmless.
 *   • sd_attack3 is essentially done swinging by ~1077 ms and then flourishes; its window stayed
 *     OPEN for ~560 ms after the blade had stopped, so late follow-through could still kill.
 * Those are real gameplay bugs, and the bake's window validator is what found them.
 *
 * ⚠ SINCE THE DMC RETIME the bake's printed 12%–88% suggestions run LONGER than the windows
 * below and that is EXPECTED — do not "correct" the table to match them. The retime made the
 * final ~20% of each clip render (action and clip end together now), so the tip-travel
 * distribution gained the follow-through flourish and the 88% percentile slid later. The
 * flourish is RECOVERY: it must not deal damage, and letting it drag the chain point later
 * would undo the retime. The windows below keep the swing-proper span; the validator
 * (coverage ≥ 60%, lead ≤ 25%) remains the honesty check and passes them.
 *
 * DERIVATION — the same rule for all three, so these are reproducible and not tuned by taste.
 * client/test/bake-hitvolumes.mts poses the real rig on the real clip, tracks the blade TIP's
 * cumulative angular travel across the portion of the clip the player actually SEES, and reports
 * the two times that bracket the swing proper:
 *   startupMs             = the action time by which 12% of total tip travel has happened
 *   startupMs + activeMs  = the action time by which 88% of total tip travel has happened
 * i.e. the window opens once the swing is genuinely underway and closes once it is spent,
 * discarding the windup below 12% and the recovery flourish above 88%. Rounded to whole ms.
 * (The bake measures in CLIP time and divides by the clip's ANIM_RATE — 1.0 for the attacks — to
 * get back to the ACTION time these fields are in. Two clocks; see above.)
 *
 * THE BAKE ONLY *SUGGESTS* THESE NUMBERS — IT DOES NOT WRITE THEM. That is on purpose and it is
 * load-bearing. This table is AUTHORITATIVE and the clip is validated against it, never the
 * reverse, so that swapping an animation asset can never silently move a damage window: it can
 * only make the bake FAIL and demand a human decision. The numbers therefore live HERE, visible
 * and diffable, and the validator (coverage >= 60%, lead <= 25%) keeps them honest from now on.
 *
 * IF YOU CHANGE OR REPLACE AN ATTACK CLIP, RE-RUN `npm run bake:hitvolumes`. It will re-check
 * these windows against the new swing and print the fresh 12%–88% suggestion to paste in.
 * ============================================================================================
 */
export const COMBAT_ACTIONS: Record<CombatActionId, CombatAction> = {
  // --- The 4-hit LIGHT COMBO (Quaternius UAL2 Sword_Light_A→D, each deployed as the pack's
  //     attack + _Rec stitched into one clip — test/convert-ual.mts). Authored AT GAME SPEED:
  //     these run at rate 1.0 (no 2.2× compensation — the old SSH swings needed it, these
  //     don't; the bake's TIMER_DIVISOR/ANIM_RATE rows are 1.0). The active window sits in
  //     the ATTACK half of the stitch; the cancel window eats the REC half — that stitch
  //     point is where a chain/dodge cancel lands, exactly the DMC contract.
  //     startupMs/activeMs are the bake's measured 12%–88% tip-travel windows. ---
  // LIGHT family A→D, ATTACK-ONLY deploys (user call 2026-07-23 — the Regular anims read
  // wrong in the string): the _Rec halves are SEPARATE combo-END recoveries — chaining
  // skips the rec (snappier chains); a chain that STOPS plays sd_lightN_rec
  // (avatar.stepAction). cancelGraceMs spans the rec + a beat so a press during the
  // recovery still chains. D is the finisher (no _Rec ships).
  sword_light_1: {
    id: "sword_light_1", clip: "sd_light1",
    durationMs: 367, // Sword_Light_A alone (its 0.50 s _Rec is the separate recovery)
    startupMs: 34, activeMs: 250, // measured 12%–88% tip travel (bake)
    cancelIntoMs: /* = startupMs + activeMs — THE CANCEL INVARIANT, see above */ 284,
    cancelGraceMs: 850, // the 0.50 s rec + a beat
    damage: BLADE_BASE_DMG,
    arc: "sword_light_1",
  },
  sword_light_2: {
    id: "sword_light_2", clip: "sd_light2",
    durationMs: 433, // Sword_Light_B alone (0.57 s rec)
    startupMs: 83, activeMs: 210, // measured
    cancelIntoMs: 293, cancelGraceMs: 920, // = startup+active; grace spans the rec
    damage: BLADE_BASE_DMG, arc: "sword_light_2",
  },
  sword_light_3: {
    id: "sword_light_3", clip: "sd_light3",
    durationMs: 867, // Sword_Light_C alone (0.70 s rec)
    startupMs: 190, activeMs: 468, // measured — C is the big sweep
    cancelIntoMs: 658, cancelGraceMs: 1050, // = startup+active; grace spans the rec
    damage: BLADE_BASE_DMG, arc: "sword_light_3",
  },
  sword_light_4: {
    id: "sword_light_4", clip: "sd_light4",
    // The FINISHER (Sword_Light_D — no _Rec ships; the clip carries its own follow-
    // through). cancelIntoMs deliberately held past the active end: a finisher that
    // re-chains instantly is a mash loop, not a beat.
    durationMs: 1667,
    startupMs: 246, activeMs: 77, // measured — the explosive slash burst (windup + settle
    //                               excluded via bake MEASURE_FROM_S/MEASURE_SPAN_S)
    cancelIntoMs: 800, cancelGraceMs: 400,
    damage: BLADE_BASE_DMG, arc: "sword_light_4",
  },
  // --- THE PARRY (RMB tap in the sword stance — Elden Ring model, user spec 2026-07-22).
  //     The ACTIVE WINDOW here is the PARRY window, not a damage window: an enemy MELEE
  //     strike or BOLT landing inside it is negated (streamed `py` flag — the server both
  //     negates and STAGGERS the melee attacker / REFLECTS the bolt). damage 0, no arc.
  //     Startup is the skill (a hit during the 80 ms raise is eaten); the long tail past
  //     the window is the WHIFF PUNISH (cancelIntoMs holds every follow-up until 780 ms). ---
  sword_parry: {
    id: "sword_parry", clip: "sd_parry",
    durationMs: 1015, // SSH Block_04_Parry 2.2333 s ÷ SWORD_PARRY_ANIM 2.2 (avatar.ts)
    startupMs: 80, activeMs: 150, // THE PARRY WINDOW [80,230] — standard Elden Ring strictness
    cancelIntoMs: 780, cancelGraceMs: 0, // the whiff recovery is the price of a bad read
    damage: 0,
  },
  // --- THE RIPOSTE (the parry's punish): LMB at a STAGGERED enemy fires this in place of
  //     the combo step — one big committed strike (UAL2 Sword_Heavy_D), 3.5× blade damage.
  //     An ordinary arced table action otherwise: same swept contact, same bake. ---
  sword_riposte: {
    id: "sword_riposte", clip: "sd_riposte",
    durationMs: 2333, // Sword_Heavy_D at 1.0× — the execution beat is the point
    startupMs: 442, activeMs: 216, // measured 12%–88% tip travel (bake)
    cancelIntoMs: 1300, cancelGraceMs: 300, // chain out through the dramatic hold
    damage: RIPOSTE_DMG, arc: "sword_riposte",
  },
  // --- THE VERTICAL GAME (DMC basic verbs). The LAUNCHER pops a grounded target airborne
  //     (High Time: RMB focus + back + LMB); the AERIAL STRING swings mid-air and RE-LIFTS
  //     the target each hit (the juggle — `launch` keeps them hanging). All three are
  //     ordinary arced table actions: same swept contact, same punch-at-active-close PvP,
  //     same bake. Windows are the bake's measured 12%–88% spans. ---
  sword_launcher: {
    id: "sword_launcher", clip: "sd_upper",
    durationMs: 700, // Sword_UpperCut at 1.0×
    startupMs: 132, activeMs: 178, // measured 12%–88% tip travel
    cancelIntoMs: 460, cancelGraceMs: 350, // held past active end (310): the launch beat breathes
    damage: BLADE_BASE_DMG, arc: "sword_launcher",
    launch: 11.4, // apex v²/2·LAUNCH_G(14) ≈ 4.6 m — 2× the authored altitude (user calls
    //              2026-07-21, twice: first 1.5×, then "even higher — 2x the original");
    //              the attacker's HIGH_TIME_RISE is 11.3, so both still crest together,
    //              and the floaty-apex gravity shaping (ai/locomotion.ts) holds them there
  },
  sword_air_1: {
    id: "sword_air_1", clip: "sd_air1",
    durationMs: 830, // Sword_Aerial_A (0.40 s) + _Rec (0.43 s)
    startupMs: 135, activeMs: 131, // measured
    cancelIntoMs: 266, cancelGraceMs: 500, // = active end; air chains breathe longer (both hang)
    damage: BLADE_BASE_DMG, arc: "sword_air_1",
    launch: 5, // the juggle re-lift — raised from 3.5 (user call 2026-07-21: air hits should
    //          hold the pair aloft longer; ~0.9 m per re-lift under LAUNCH_G, and the longer
    //          ballistic stint IS the longer hitstun — AI stays suppressed while airborne)
  },
  sword_air_2: {
    id: "sword_air_2", clip: "sd_air2",
    durationMs: 570, // Sword_Aerial_B (no _Rec ships)
    startupMs: 235, activeMs: 139, // measured — B is a spin; its blade lands late by design
    cancelIntoMs: 374, cancelGraceMs: 500,
    damage: BLADE_BASE_DMG, arc: "sword_air_2",
    launch: 5, // (same raise as air_1)
  },
  sword_gpound: {
    id: "sword_gpound", clip: "sd_gpound",
    // The AIR-STRING FINISHER (Sword_GroundPound): the attacker slams straight down
    // (character.slamDown) and the blade DRAGS THE JUGGLED TARGET with it — negative
    // launch spikes an airborne body floorward, so both arrive together and the target
    // eats the knockdown. The string ends here (no wrap; land to reset).
    durationMs: 1170,
    startupMs: 45, activeMs: 889, // measured — the blade stays live through the whole dive,
    //                               catching the target on the way down (the drag)
    cancelIntoMs: 934, cancelGraceMs: 0,
    damage: BLADE_BASE_DMG, arc: "sword_gpound",
    launch: -12,
  },
  // (The charge-plunge "sword_air" action was REMOVED 2026-07-21 — the aerial STRING
  //  (sword_air_1/2 + sword_gpound, all arced) is the whole air kit now.)
  // --- Dodges: no hit volume, no damage. They are in the table because item 3 hangs
  //     movementCurve on them, and because the server must know an i-frame window is live. ---
  sword_dodge_f: { id: "sword_dodge_f", clip: "sd_dodge_f", durationMs: 696, startupMs: 0, activeMs: 0, damage: 0, iframeMs: [0, EVADE_IFRAME_MS] },
  sword_dodge_b: { id: "sword_dodge_b", clip: "sd_dodge_b", durationMs: 696, startupMs: 0, activeMs: 0, damage: 0, iframeMs: [0, EVADE_IFRAME_MS] },
  sword_dodge_l: { id: "sword_dodge_l", clip: "sd_dodge_l", durationMs: 598, startupMs: 0, activeMs: 0, damage: 0, iframeMs: [0, EVADE_IFRAME_MS] },
  sword_dodge_r: { id: "sword_dodge_r", clip: "sd_dodge_r", durationMs: 598, startupMs: 0, activeMs: 0, damage: 0, iframeMs: [0, EVADE_IFRAME_MS] },
  // ROLLS = THE dodge now (avatar.swordDodge; the sd_dodge_* rows above are retired from play
  // but stay for wire compat). The ORIGINAL SSH Roll family, all six dirs (the UAL dodge/
  // flip/reversed experiments were reverted 2026-07-21). Durations = full clip at
  // SWORD_ROLL_ANIM 1.0 (bake-validated); move-cancel trims the tails.
  sword_roll_f:  { id: "sword_roll_f",  clip: "sd_roll_f",  durationMs: 1100, startupMs: 0, activeMs: 0, damage: 0, iframeMs: [0, EVADE_IFRAME_MS] },
  sword_roll_b:  { id: "sword_roll_b",  clip: "sd_roll_b",  durationMs: 1100, startupMs: 0, activeMs: 0, damage: 0, iframeMs: [0, EVADE_IFRAME_MS] },
  sword_roll_l:  { id: "sword_roll_l",  clip: "sd_roll_l",  durationMs: 1100, startupMs: 0, activeMs: 0, damage: 0, iframeMs: [0, EVADE_IFRAME_MS] },
  sword_roll_r:  { id: "sword_roll_r",  clip: "sd_roll_r",  durationMs: 1100, startupMs: 0, activeMs: 0, damage: 0, iframeMs: [0, EVADE_IFRAME_MS] },
  // Back-diagonals (SSH Roll_BL/BR — user call 2026-07-21): same profile, true diagonal clips.
  sword_roll_bl: { id: "sword_roll_bl", clip: "sd_roll_bl", durationMs: 1083, startupMs: 0, activeMs: 0, damage: 0, iframeMs: [0, EVADE_IFRAME_MS] },
  sword_roll_br: { id: "sword_roll_br", clip: "sd_roll_br", durationMs: 1083, startupMs: 0, activeMs: 0, damage: 0, iframeMs: [0, EVADE_IFRAME_MS] },
  // --- THE 2H GREATSWORD (Valheim-style mode — docs/2h-combat-spec.md; combat-lab only for
  //     now, toggled by KeyG). Pack: Essential S&S UE5/2H, deployed TRIMMED to action length
  //     by test/deploy-2h.mts (the raw clips pad a 1.5–2 s idle tail past the swing; trim ==
  //     durationMs at rate 1.0, so no TIMER_DIVISOR games). Chain picked by MEASURED lateral
  //     continuity (Attack_07 ends +0.35 → Attack_08 starts +0.35 exactly → Attack_06 raises
  //     the overhead finisher); startup/active are the bake's measured tip-travel windows.
  //     Valheim contract: attacks are COMMIT-AND-PRAY — cancelIntoMs is the CHAIN point only;
  //     dodge/block never interrupt (the gs decision layer simply doesn't offer the cancel).
  //     Stamina costs + stagger forces live in COMBAT_2H (combat2h.ts), keyed by these ids. ---
  gs_light_1: {
    id: "gs_light_1", clip: "gs_light1",
    durationMs: 700, // Attack_07 trimmed (swing settles at 392 ms; the rest is recovery beat)
    startupMs: 191, activeMs: 135, // measured 12%–88% tip travel (bake)
    cancelIntoMs: 392, cancelGraceMs: 350, // chain executes at the measured SETTLE
    damage: BLADE_BASE_DMG, arc: "gs_light_1",
  },
  gs_light_2: {
    id: "gs_light_2", clip: "gs_light2",
    durationMs: 720, // Attack_08 trimmed (settle 411)
    startupMs: 142, activeMs: 183, // measured
    cancelIntoMs: 411, cancelGraceMs: 350,
    damage: BLADE_BASE_DMG, arc: "gs_light_2",
  },
  gs_light_3: {
    id: "gs_light_3", clip: "gs_light3",
    // The FINISHER — the tallest overhead of the set (blade 0.50–1.91 m), doubled windup:
    // the Valheim third-hit payoff. cancelIntoMs held past the active end — a finisher
    // that re-chains instantly is a mash loop, not a beat.
    durationMs: 950, // Attack_06 trimmed (settle 604)
    startupMs: 425, activeMs: 167, // measured
    cancelIntoMs: 800, cancelGraceMs: 350,
    damage: Math.round(BLADE_BASE_DMG * 1.4), arc: "gs_light_3",
  },
  gs_heavy: {
    id: "gs_heavy", clip: "gs_heavy",
    // The SECONDARY: Attack_11's stepping overhead heave — 522 ms windup, 1.8 m step-in.
    // The slow, expensive door-breaker (2.2× damage, the big stagger hit).
    durationMs: 1083, // trimmed (the 1.1 s trim lands on the last 60 fps key, 1.0833 s)
    startupMs: 480, activeMs: 172, // measured
    cancelIntoMs: 850, cancelGraceMs: 300, // committal: held well past the active end (652)
    damage: Math.round(BLADE_BASE_DMG * 2.2), arc: "gs_heavy",
  },
};

/** LMB chain order. Wraps past the finisher (matches the old SWORD_COMBO). */
export const SWORD_COMBO_CHAIN: readonly CombatActionId[] = [
  "sword_light_1", "sword_light_2", "sword_light_3", "sword_light_4",
];

/** AERIAL chain order (airborne TAP LMB): A → B → the GroundPound SLAM, then the string is
 *  SPENT (no wrap — you land with your target). Each pre-slam hit re-lifts the juggled
 *  victim (`launch`) and floats the attacker (character.aerialHop); the slam drags both
 *  down. Between hits the attacker holds the floating sd_air_idle stance. */
/** AERIAL tap chain (airborne LMB): A ↔ B, WRAPPING — the juggle sustains as long as you
 *  can keep them up. The GroundPound is NOT in the tap chain any more (user call
 *  2026-07-23): it fires only on a HELD LMB (GPOUND_HOLD_S in the ActionSystem), chaining
 *  in at the current air hit's cancel point — the slam is a decision, not a default. */
export const SWORD_AIR_CHAIN: readonly CombatActionId[] = ["sword_air_1", "sword_air_2"];

/** 2H GREATSWORD chain (Valheim 1-2-slam): sweep → return sweep → overhead finisher, wraps
 *  to hit 1 past the finisher. The Valheim-layer mechanics (stamina costs, stagger forces,
 *  block/parry constants) live in COMBAT_2H / COMBAT_2H_SYSTEM (combat2h.ts), same ids. */
export const GS_COMBO_CHAIN: readonly CombatActionId[] = ["gs_light_1", "gs_light_2", "gs_light_3"];

/** Safe lookup for untrusted input (a client-supplied action id). Unknown → null → reject.
 *  hasOwnProperty, not a plain index: a bare `COMBAT_ACTIONS[id] ?? null` hands back
 *  Object.prototype for id === "__proto__" (and Function for "constructor"), which is
 *  NOT nullish — so a hostile client could smuggle a non-action past the server's guard. */
export function combatAction(id: string): CombatAction | null {
  if (!Object.prototype.hasOwnProperty.call(COMBAT_ACTIONS, id)) return null;
  return (COMBAT_ACTIONS as Record<string, CombatAction>)[id] ?? null;
}

/** True while `elapsedMs` into `a` sits inside its active hit window. */
export function isActive(a: CombatAction, elapsedMs: number): boolean {
  return a.arc !== undefined && elapsedMs >= a.startupMs && elapsedMs < a.startupMs + a.activeMs;
}

/** Normalized position (0..1) within the active window — the `t` HIT_ARCS is keyed on. */
export function activeT(a: CombatAction, elapsedMs: number): number {
  if (a.activeMs <= 0) return 0;
  const t = (elapsedMs - a.startupMs) / a.activeMs;
  return t < 0 ? 0 : t > 1 ? 1 : t;
}
